import { useCallback, useMemo, useRef } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { NextBestActionType, useMarketConfig } from '~/app/market-config';
import { NextBestActionsStyle } from '~/app/pages/summary/components/next-best-actions/styles/next-best-actions.style';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction, NbaTargetTypes } from '~/app/tracking/feld-m-types';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { AudiCode } from './components/audi-code';
import { PrimaryNextBestAction, SecondaryNextBestAction } from './components/next-best-action';

export function NextBestActions() {
	const { isLoading } = useConfigurator();
	const { config } = useMarketConfig();
	const primaryAreaRef = useRef<HTMLDivElement>(null);
	const secondaryAreaRef = useRef<HTMLDivElement>(null);
	const { i18n } = useI18n();
	const {
		config: { nextBestActions, additionalPrimaryNba, disablePdfNba },
	} = useMarketConfig();

	const pdfNba: NextBestActionType = {
		icon: 'document-pdf',
		labelText: i18n('ngc.actions.savePDF'),
		trackingName: NbaTargetTypes.PDF,
		url: config.printServiceUrl?.url,
		target: '_blank',
		description: '',
	};

	const audiCodeNba: NextBestActionType = {
		icon: 'code',
		labelText: i18n('ngc.actions.saveConfiguration'),
		trackingName: NbaTargetTypes.ConfigurationSave,
		url: '',
		target: '_blank',
		description: '',
	};

	const nbaNodes = useMemo(
		() => [
			...Array.from(primaryAreaRef.current?.childNodes || []),
			...Array.from(secondaryAreaRef.current?.childNodes || []),
		],
		[primaryAreaRef.current, secondaryAreaRef.current],
	);

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && nbaNodes.length) {
				appTrackingService.summaryImpression({
					node,
					eventName: 'configurator (NBA) - impression',
					componentName: 'configurator-next-best-actions',
					relatedElements: nbaNodes.map((nbaNode, index) => ({
						elementName: 'cta',
						elementValue: `${index + 1}-${(nbaNode as HTMLElement).id}`,
					})),
				});
			}
		},
		[isLoading, nbaNodes],
	);

	const trackNbaClick = useCallback(
		(nba: NextBestActionType, targetUrl: string, elementName: string) => {
			const nodeIndex = nbaNodes.findIndex((nbaNode) => (nbaNode as HTMLElement).id === nba.trackingName) + 1;
			const eventAction = nba.trackingName === 'pdf' ? EventAction.Download : EventAction.Content;

			appTrackingService.nbaClickEvent({
				eventAction,
				nbaName: nba.trackingName,
				label: nba.labelText,
				targetUrl,
				clickID: nba.icon,
				elementName,
				elementValue: `${nodeIndex}-${nba.trackingName}`,
			});
		},
		[nbaNodes],
	);

	return (
		<NextBestActionsStyle ref={impressionTrackingRef}>
			<div ref={primaryAreaRef} className={'primary-area'}>
				{additionalPrimaryNba && (
					<PrimaryNextBestAction trackNbaClick={trackNbaClick} nba={additionalPrimaryNba} />
				)}
				<AudiCode nba={audiCodeNba} trackNbaClick={trackNbaClick} />
				{!disablePdfNba && <PrimaryNextBestAction trackNbaClick={trackNbaClick} nba={pdfNba} />}
			</div>

			<div ref={secondaryAreaRef} className={'secondary-area'}>
				{nextBestActions.map((nba) => (
					<SecondaryNextBestAction key={nba.labelText} nba={nba} trackNbaClick={trackNbaClick} />
				))}
			</div>
		</NextBestActionsStyle>
	);
}
